import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getUrl } from "aws-amplify/storage";
import {
  Box,
  Grid,
  Button,
  TextField,
  Autocomplete,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Snackbar,
  Alert,
  Container,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";

import {
  listCategories,
  listManifestItems,
  listProviders,
  listContents,
  getContent,
} from "../../graphql/queries";

import {
  createManifestItem,
  deleteManifestItem,
} from "../../graphql/mutations";
import { toast } from "react-toastify";
import DeleteManifestItem from "../../components/DeleteManifestItem";

const client = generateClient();

const INITIAL_FORM_STATE = {
  manifestID: "",
  providerId: "",
  categoryItemId: "",
  type: "",
  title: "",
  category: "",
  contentType: "",
  SeasonId: "",
  contentId: "",
  poster: "",
  isActive: false,
};

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  "& .MuiCardContent-root": {
    flexGrow: 1,
  },
}));

export default function AddManifestItems() {
  const navigate = useNavigate();
  const location = useLocation();
  const manifestId = location.state?.userId;

  // State management
  const [formData, setFormData] = useState({
    ...INITIAL_FORM_STATE,
    manifestID: manifestId,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [manifestItems, setManifestItems] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const [providerList, setProviderList] = useState([]);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const DATA_GRID_COLUMNS = [
    { field: "title", headerName: "Title", flex: 1 },
    { field: "type", headerName: "Type", width: 150 },
    { field: "category", headerName: "Category", width: 150 },
    { field: "director", headerName: "Director", width: 200 },
    { field: "language", headerName: "Language", width: 150 },
    { field: "isActive", headerName: "Is Active", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <DeleteManifestItem
          setManifestItems={setManifestItems}
          id={params?.row?.id}
          client={client}
        />
      ),
    },
  ];

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  const handleSearch = useCallback(async () => {
    if (!formData.category) {
      showToast("Please select a category first.", "warning");
      return;
    }

    try {
      const response = await client.graphql({
        query: listContents,
        variables: {
          limit: 10000, 

          filter: {
            category: { eq: formData.category },
            ...(searchTerm && {
              title: { contains: searchTerm.toLowerCase() },
            }),
          },
        },
      });

      const filteredResults = response.data.listContents.items.filter(
        (item) =>
          !manifestItems?.some((manifest) => manifest.contentId === item.id)
      );

      if (!filteredResults.length) {
        showToast("No results found.", "info");
        setSearchResults([]);
        return;
      }

      const enhancedResults = await Promise.all(
        filteredResults.map(async (item) => {
          if (item?.posters && item?.posters?.length) {
            return {
              ...item,
              posterUrls: await Promise.all(
                item.posters.map((poster) =>
                  getUrl({
                    key: poster,
                    options: { validateObjectExistence: true },
                  }).catch(console.error)
                )
              ),
            };
          } else {
            return {
              ...item,
              posterUrls: [],
            };
          }
        })
      );

      setSearchResults(enhancedResults);
    } catch (error) {
      console.error("Search error:", error);
      showToast("Error occurred while searching.", "error");
    }
  }, [formData.category, searchTerm, manifestItems]);

  const handleAddItem = useCallback(
    async (item) => {
      try {
        await client.graphql({
          query: createManifestItem,
          variables: {
            input: {
              contentId: item.id,
              manifestID: manifestId,
              providerId: formData.providerId,
              isActive: true,
            },
          },
        });

        showToast("Item added successfully!", "success");
        await fetchManifestItems();
        setSearchResults((prev) =>
          prev.filter((result) => result.id !== item.id)
        );
      } catch (error) {
        console.error("Add item error:", error);
        showToast("Error adding item.", "error");
      }
    },
    [formData.providerId, manifestId]
  );

  const fetchManifestItems = useCallback(async () => {
    try {
      const response = await client.graphql({
        query: listManifestItems,
        variables: { filter: { manifestID: { eq: manifestId } } },
      });

      const items = response.data.listManifestItems.items;
      const itemsWithDetails = await Promise.all(
        items.map(async (item) => {
          const contentResponse = await client.graphql({
            query: getContent,
            variables: { id: item.contentId },
          });
          return {
            ...item,
            ...contentResponse.data.getContent,
          };
        })
      );

      setManifestItems(itemsWithDetails);
    } catch (error) {
      console.error("Fetch manifest items error:", error);
      showToast("Error fetching manifest items.", "error");
    }
  }, [manifestId]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [categoriesResponse, providersResponse] = await Promise.all([
          client.graphql({ query: listCategories }),
          client.graphql({ query: listProviders }),
        ]);

        setCategoryList(
          categoriesResponse.data.listCategories.items.map((category) => ({
            label: category.title,
            value: category.id,
          }))
        );

        setProviderList(
          providersResponse.data.listProviders.items.map((provider) => ({
            label: provider.name,
            value: provider.id,
          }))
        );

        await fetchManifestItems();
      } catch (error) {
        console.error("Initial data fetch error:", error);
        showToast("Error loading initial data.", "error");
      }
    };

    fetchInitialData();
  }, []);

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={categoryList}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Category"
                  required
                  fullWidth
                />
              )}
              onChange={(_, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  category: newValue?.label || "",
                }));
                setSearchResults([]);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={providerList}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField {...params} label="Provider" required fullWidth />
              )}
              onChange={(_, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  providerId: newValue?.value || "",
                }));
              }}
            />
          </Grid>

          {
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  sx={{ minWidth: 120 }}
                >
                  Search
                </Button>
              </Box>
            </Grid>
          }
        </Grid>

        {/* Search Results */}
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {searchResults.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <StyledCard>
                <CardMedia
                  component="img"
                  height="200"
                  image={
                    item.posterUrls[0]?.url.href || "/placeholder-image.jpg"
                  }
                  alt={item.title}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Type: {item.type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Genre: {item.genre?.join(", ")}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleAddItem(item)}
                    >
                      Add Item
                    </Button>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>

        {/* Manifest Items Grid */}
        {manifestItems && (
          <Box sx={{ height: 400, width: "100%", mt: 4 }}>
            <DataGrid
              rows={manifestItems}
              columns={DATA_GRID_COLUMNS}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              checkboxSelection
              disableSelectionOnClick
            />
          </Box>
        )}

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={() => setToast({ ...toast, open: false })}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
